import React, { useState, useRef, useEffect } from "react";
import ReactCrop, { Crop, centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import { PulseLoader } from "react-spinners";

interface ImageCropperProps {
  imageFile: File;
  onCropComplete: (croppedFile: File) => void;
  onCancel?: () => void;
  aspect?: number;
}

const ImageCropper: React.FC<ImageCropperProps> = ({ imageFile, onCropComplete, onCancel, aspect = 1 }) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop | undefined>();
  const [completedCrop, setCompletedCrop] = useState<Crop | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!imageFile) return;

    let cancelled = false;

    const normalizeTo34Blur = (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            const ow = img.naturalWidth;
            const oh = img.naturalHeight;

            let cw = ow;
            let ch = Math.round(ow / aspect);
            if (ch < oh) {
              ch = oh;
              cw = Math.round(oh * aspect);
            }

            const canvas = document.createElement("canvas");
            canvas.width = cw;
            canvas.height = ch;
            const ctx = canvas.getContext("2d");
            if (!ctx) return reject("No context");

            const bgScale = Math.max(cw / ow, ch / oh);
            const bgW = ow * bgScale;
            const bgH = oh * bgScale;
            const bgX = (cw - bgW) / 2;
            const bgY = (ch - bgH) / 2;

            ctx.filter = "blur(20px) brightness(1)";
            ctx.drawImage(img, bgX, bgY, bgW, bgH);

            ctx.filter = "none";

            const fgScale = Math.min(1, cw / ow, ch / oh);
            const fgW = ow * fgScale;
            const fgH = oh * fgScale;
            const fgX = (cw - fgW) / 2;
            const fgY = (ch - fgH) / 2;

            ctx.drawImage(img, fgX, fgY, fgW, fgH);

            resolve(canvas.toDataURL("image/jpeg", 0.92));
          };
          img.src = reader.result as string;
        };
        reader.readAsDataURL(file);
      });
    };

    normalizeTo34Blur(imageFile).then((data) => {
      if (!cancelled) setSelectedImage(data);
    });

    return () => {
      cancelled = true;
    };
  }, [imageFile, aspect]);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const initial = centerCrop(
      makeAspectCrop({ unit: "%", width: 80 }, aspect, width, height),
      width,
      height
    );
    setCrop(initial);
  };

  const getCroppedImage = async () => {
    if (!imgRef.current || !completedCrop) return;

    const canvas = document.createElement("canvas");
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;


    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(
      imgRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    canvas.toBlob((blob) => {
      if (blob) {
        const timestamp = new Date().getTime();
        const originalName = imageFile.name.split('.')[0];
        const fileName = `${originalName}_cropped_${timestamp}.jpeg`;
        const file = new File([blob], fileName, { type: "image/jpeg" });
        onCropComplete(file);
      }
    }, "image/jpeg");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      <div style={{
        padding: "14px 20px",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: 8,
          background: "linear-gradient(135deg, rgba(238,63,124,0.2), rgba(168,85,247,0.15))",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#EE3F7C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/>
          </svg>
        </div>
        <span style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.9)", letterSpacing: "0.02em", fontFamily: "monospace", textTransform: "uppercase" }}>
          Crop Image
        </span>
      </div>

      {selectedImage ? (
        <>
          <div style={{
            padding: 16,
            display: "flex",
            justifyContent: "center",
            background: "rgba(255,255,255,0.02)",
          }}>
            <div style={{
              borderRadius: 12,
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
              display: "inline-block",
            }}>
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspect}
                keepSelection
              >
                <img
                  ref={imgRef}
                  src={selectedImage}
                  alt="Crop me"
                  className="cropper-image"
                  onLoad={onImageLoad}
                  style={{ maxHeight: "50vh", maxWidth: "100%", objectFit: "contain", display: "block" }}
                />
              </ReactCrop>
            </div>
          </div>

          <div style={{
            display: "flex",
            gap: 12,
            justifyContent: "flex-end",
            padding: "14px 20px",
            borderTop: "1px solid rgba(255,255,255,0.06)",
            background: "rgba(255,255,255,0.02)",
          }}>
            {onCancel && (
              <button className="v3-btn-secondary" onClick={onCancel}>
                Cancel
              </button>
            )}
            <button onClick={getCroppedImage} className="v3-btn-primary">
              Apply Crop
            </button>
          </div>
        </>
      ) : (
        <div style={{
          padding: 48,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
          background: "rgba(255,255,255,0.02)",
        }}>
          <PulseLoader color="#EE3F7C" size={8} />
          <span style={{ fontSize: 11, color: "rgba(255,255,255,0.35)" }}>Preparing image…</span>
        </div>
      )}
    </div>
  );
};

export default ImageCropper;